import React, { useState, useEffect } from 'react';
import uploadService from '../services/uploadService';
import { useToast } from '../contexts/ToastContext';
import './Feeds.css';

const AdminUploading = () => {
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cleaning, setCleaning] = useState(false);
    const toast = useToast();

    useEffect(() => {
        // Auto-cleanup stale sessions on page load, then fetch
        runCleanup(true).then(() => fetchUploads());
        const interval = setInterval(fetchUploads, 5000);
        return () => clearInterval(interval);
    }, []);

    const fetchUploads = async () => {
        try {
            const result = await uploadService.getAllUploads({ status: 'uploading' });
            if (result.success) {
                setUploads(result.uploads);
            }
        } catch (error) {
            console.error('Error fetching uploads:', error);
        } finally {
            setLoading(false);
        }
    };

    const runCleanup = async (silent = false) => {
        try {
            setCleaning(true);
            const result = await uploadService.cleanupStale();
            if (result.success && !silent) {
                toast.success(result.count > 0 ? `Cleared ${result.count} stale upload(s)` : 'No stale uploads found');
                if (result.count > 0) fetchUploads();
            }
        } catch (error) {
            console.error('Error cleaning stale uploads:', error);
            if (!silent) {
                const msg = error.response?.data?.message || error.message || 'Unknown error';
                toast.error(`Failed to clean stale uploads: ${msg}`);
            }
        } finally {
            setCleaning(false);
        }
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const formatDuration = (startTime) => {
        const start = new Date(startTime);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);

        if (diff < 60) return `${diff}s`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
        return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
    };

    if (loading) {
        return (
            <div className="feeds-page">
                <div className="table-section">
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading active uploads...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="feeds-page">
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button
                    className="btn btn-warning"
                    onClick={() => runCleanup(false)}
                    disabled={cleaning}
                >
                    <i className="fas fa-broom"></i> {cleaning ? 'Cleaning...' : 'Clean Up Stale'}
                </button>
            </div>
            <div className="table-section">
                {uploads.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-check-circle" style={{ fontSize: '48px', color: '#10b981', opacity: 0.2, marginBottom: '16px' }}></i>
                        <p>No active uploads</p>
                        <p className="empty-hint">All uploads have been completed</p>
                    </div>
                ) : (
                    <>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>AWB</th>
                                    <th>User</th>
                                    <th>Scan Type</th>
                                    <th>Started</th>
                                    <th>Duration</th>
                                    <th>Progress</th>
                                </tr>
                            </thead>
                            <tbody>
                                {uploads.map(upload => (
                                    <tr key={upload.uploadId}>
                                        <td className="awb-cell">{upload.awb}</td>
                                        <td className="user-cell">
                                            <span className="name">{upload.userName || 'Unknown'}</span>
                                        </td>
                                        <td>
                                            <span className={`scan-badge ${upload.scanType}`}>
                                                {upload.scanType}
                                            </span>
                                        </td>
                                        <td className="date-cell">{formatTime(upload.startTime)}</td>
                                        <td>{formatDuration(upload.startTime)}</td>
                                        <td>
                                            <div className="progress-cell">
                                                <div className="mini-progress">
                                                    <div
                                                        className="mini-progress-fill"
                                                        style={{ width: `${upload.progress || 0}%` }}
                                                    ></div>
                                                </div>
                                                <span>{upload.progress || 0}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminUploading;
